// The fee reserve panel (docs/08-app-design.md 5.7, called the gas tank
// there; website build brief section 11): the fee key's address with QR and
// copy, its balance against what the next step needs (round-4 brief section 8:
// adequacy for the step at hand, not an estimate of thousands of future
// actions), and the ways to top it up. A balance that could not be read is
// said to be unread, never shown as zero. Against a local cluster (the rpc
// shim or a test validator) an airdrop button appears; never against a
// public host. The fee key (the relayer in code) never holds vault funds,
// and the panel says so.
//
// The figure after "The next step needs at least" is always the engine's own
// requirement for that step, read live (docs/18-one-fee-reserve.md 5.1 A;
// R78): it includes the reserve's account minimum F. A suggested amount, when
// a page passes one, is labelled as a suggestion beside it, never as a need.
//
// The reserve is the vault's own, derived from the 24 words (docs/18 2.4):
// every device that opens the vault uses it. Its address, QR code and the
// airdrop appear only once `engine.gasTank()` has answered, since that runs
// the network check first (2.5); a refusal shows the check's sentence in
// their place. The panel carries the compromise disclosure (section 7).

import { useCallback, useEffect, useRef, useState } from 'react';
import { WRONG_NETWORK_SENTENCE, WrongNetwork } from '../deployment';
import { type Engine, LAMPORTS_PER_ACTION, type ReserveReading, isLocalRpc } from '../engine';
import { useSession } from '../session';
import { Address, Amount, Button, Notice, Qr, StatusLine, useToast, Toast } from './primitives';
import { FeeCapsule } from './FeeCapsule';

/**
 * The compromise disclosure while Part B is deferred (docs/18 section 7, owner decision 1): the reviewed paragraph,
 * word for word, so do not reword it here. Section 7 names exactly four places: this panel (below "Your 24 words
 * restore it."), Settings' "This device" section, the public site's "What stays outside" (Protection.tsx) and
 * docs/08 5.7; the first three render this constant.
 */
export const FEE_RESERVE_DISCLOSURE =
  "If someone else gets your fee reserve's key, this version of the app cannot replace it yet. That key can spend only the SOL in the fee reserve. It cannot sign for your vault or move your vault's funds; at most it can submit a swap you already signed, within the limits you approved. If you think the key is exposed: stop sending SOL to the fee reserve, because anything you add can be taken. Your vault's funds do not need to move. Wait for the release that replaces the fee reserve. Until then the app may be unable to pay for any action, moving funds out included, whenever that reserve is emptied.";

/**
 * The fee reserve's reading. `network` is the network check of the latest answered read (docs/18 2.5): 'ok' once
 * `gasTank()` got past it (its balance read may still have failed), 'wrong' when it refused, null before any answer.
 * A page shows the reserve's address as a funding destination, or opens a wallet send to it, only while it is 'ok'.
 */
export function useGasTank(engine: Engine | null) {
  const [tank, setTank] = useState<ReserveReading | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [network, setNetwork] = useState<'ok' | 'wrong' | null>(null);
  const refresh = useCallback(async () => {
    if (!engine) return;
    try {
      setTank(await engine.gasTank());
      setError(null);
      setNetwork('ok');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      // gasTank() checks the network before any read, and that check throws only WrongNetwork: any other failure
      // came after it passed. A refusal also drops the last figure, so no balance is shown as current on a network
      // this build was not made for.
      if (e instanceof WrongNetwork) {
        setTank(null);
        setNetwork('wrong');
      } else {
        setNetwork('ok');
      }
    }
  }, [engine]);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  return { tank, error, network, refresh };
}

/** What the panel says of the reserve whatever the network check answered (docs/18 section 5, "Wording"; section 7). */
function AboutReserve() {
  return (
    <>
      <p>The fee reserve pays network fees and account rent only. It never holds vault funds. Every device that opens this vault uses this fee reserve. Your 24 words restore it.</p>
      <p>{FEE_RESERVE_DISCLOSURE}</p>
    </>
  );
}

export function GasTankPanel({
  engine,
  needed,
  suggested,
  onFunded,
  onBalance,
}: {
  engine: Engine;
  /** The engine's requirement for the next step (activationCost(), refreshCost(), plainActionCost()), or null while it is read. */
  needed: bigint | null;
  /** A suggested amount, shown labelled as one beside the requirement (Deposit's ten-action allowance); never a gate. */
  suggested?: bigint | null;
  onFunded?: () => void;
  onBalance?: (lamports: bigint) => void;
}) {
  const { settings } = useSession();
  const { tank, error, network, refresh } = useGasTank(engine);
  const [busy, setBusy] = useState(false);
  const [toast, say] = useToast();
  const enough = tank !== null && needed !== null && tank.lamports >= needed;
  // Fire once per transition into "enough", never once per render: callers pass inline callbacks.
  const fired = useRef(false);
  useEffect(() => {
    if (enough && !fired.current) {
      fired.current = true;
      onFunded?.();
    }
    if (!enough) fired.current = false;
  }, [enough, onFunded]);
  const lamports = tank?.lamports;
  useEffect(() => {
    if (lamports !== undefined) onBalance?.(lamports);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lamports]);
  const airdrop = async () => {
    setBusy(true);
    try {
      await engine.airdrop(needed !== null && needed > 100_000_000n ? needed : 100_000_000n);
      await refresh();
      say('Airdrop landed');
    } catch (e) {
      say(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  if (network !== 'ok') {
    // No address, QR code or airdrop before the network check has passed (docs/18 2.5).
    return (
      <Notice label="Fee reserve">
        <div className="stack tight">
          {network === 'wrong' ? (
            <StatusLine state="failed" word="Wrong network.">
              {WRONG_NETWORK_SENTENCE}
            </StatusLine>
          ) : (
            <p>Checking the network, then reading the balance…</p>
          )}
          <AboutReserve />
          <div className="actions">
            <Button variant="ghost" small onClick={() => void refresh()}>
              Check again
            </Button>
          </div>
        </div>
      </Notice>
    );
  }
  return (
    <Notice label="Fee reserve" tone={enough ? 'accent' : undefined}>
      <div className="address-block fee-card">
        {/* the capsule follows the panel's own status line (SCENES B10 "Card"; APP-R3 §5) */}
        <FeeCapsule size="card" state={enough ? 'funded' : tank === null && error ? 'error' : 'idle'} />
        <div className="stack tight">
          <Address value={engine.relayerAddress} kind="fee reserve" full onCopied={() => say('Copied')} />
          <details className="qr-disclosure">
            <summary>
              <span className="qr-show">Show QR code</span>
              <span className="qr-hide">Hide QR code</span>
            </summary>
            <Qr text={engine.relayerAddress} label="QR code of the fee reserve address" />
          </details>
          <p>
            {tank ? (
              <>
                Holds <Amount raw={tank.lamports} decimals={9} symbol="SOL" />.{' '}
                {needed !== null ? (
                  <>
                    The next step needs at least <Amount raw={needed} decimals={9} symbol="SOL" maxFraction={9} />.
                  </>
                ) : (
                  'Working out what the next step needs…'
                )}
                {suggested ? (
                  <>
                    {' '}
                    Suggested: <Amount raw={suggested} decimals={9} symbol="SOL" maxFraction={9} />, enough for about ten actions.
                  </>
                ) : null}
              </>
            ) : error ? (
              `Could not read the balance: ${error}`
            ) : (
              'Reading the balance…'
            )}
          </p>
          <AboutReserve />
          <div className="actions">
            <Button variant="ghost" small onClick={() => void refresh()}>
              Check again
            </Button>
            {isLocalRpc(settings.rpcUrl) ? (
              <Button variant="ghost" small onClick={() => void airdrop()} disabled={busy} working={busy}>
                Airdrop (local cluster)
              </Button>
            ) : null}
          </div>
          {enough ? (
            <StatusLine state="landed" word="Funded.">
              It holds enough for the next step.
            </StatusLine>
          ) : tank === null && error ? (
            <StatusLine state="neutral" word="Could not check.">
              The balance could not be read, so it is not shown as zero. Check again in a moment.
            </StatusLine>
          ) : (
            <StatusLine state="neutral" word="Waiting.">
              Add SOL to your fee reserve at the address above from any wallet, then check again. About {formatActions(LAMPORTS_PER_ACTION)} per action.
            </StatusLine>
          )}
        </div>
      </div>
      <Toast message={toast} />
    </Notice>
  );
}

function formatActions(perAction: bigint): string {
  return `${(Number(perAction) / 1e9).toFixed(6).replace(/0+$/, '')} SOL`;
}
